import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { pickPhotoOfDay, type Photo, type PhotoMarks, type PhotoOfDay } from "../../../shared/photos";
import { getDevicePrefs } from "../../app/devicePrefs";
import { api, ApiRequestError } from "../../data/api";
import { useMe } from "../../data/queries";

const MIN = 60_000;

/**
 * Family photos come from the wall computer's own copy (the photo agent on the
 * household's PC chooses them and keeps it up to date; docs/MEDIA.md). Kinmantle
 * never stores them.
 *
 * - On the wall computer itself, straight from its loopback server.
 * - Anywhere else (a phone, a laptop, away from home), through Kinmantle's relay
 *   for signed-in family; photos pass through Cloudflare but aren't stored there.
 * - The demo always uses the relay, which answers with drawn sample photos: on the
 *   wall computer the loopback copy would be the household's own.
 */
const LOCAL = "http://127.0.0.1:47200";
const RELAY = "/api/photos/feed";
const PROBE_EVERY_MS = 10 * MIN;

let resolved: { base: string; at: number } | null = null;

async function feedBase(demo: boolean): Promise<string> {
  if (demo || !getDevicePrefs().wallComputer) return RELAY;
  if (resolved && Date.now() - resolved.at < PROBE_EVERY_MS) return resolved.base;
  let base = RELAY;
  try {
    const res = await fetch(`${LOCAL}/v1/ping`, { signal: AbortSignal.timeout(1500), cache: "no-store" });
    if (res.ok) base = LOCAL;
  } catch {
    /* not on the wall computer after all, or its photo server is down: use the relay */
  }
  resolved = { base, at: Date.now() };
  return base;
}

async function feed<T>(demo: boolean, path: string, params: Record<string, string>): Promise<{ base: string; body: T | null }> {
  const base = await feedBase(demo);
  const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "")).toString();
  let res: Response;
  try {
    res = await fetch(`${base}/v1/${path}${qs ? `?${qs}` : ""}`, { credentials: "same-origin", cache: "no-store", headers: { accept: "application/json" } });
  } catch {
    if (base === LOCAL) resolved = null; // try the relay next time
    throw new ApiRequestError("You appear to be offline.", 0, "OFFLINE");
  }
  // No wall computer set up yet: an empty library, not an error.
  if (res.status === 503) return { base, body: null };
  if (!res.ok) {
    let message = `Photos couldn't be loaded (${res.status})`;
    try {
      message = ((await res.json()) as { error?: string }).error ?? message;
    } catch {
      /* not JSON */
    }
    throw new ApiRequestError(message, res.status, "PHOTOS");
  }
  return { base, body: (await res.json()) as T };
}

/** Image addresses in the feed are relative to /v1/ (demo photos are absolute paths). */
function resolve(base: string, p: Photo): Photo {
  const at = (u: string) => (/^(https?:)?\//.test(u) ? u : `${base}/v1/${u}`);
  return { ...p, url: at(p.url), thumbUrl: at(p.thumbUrl) };
}

export interface PhotoQuery {
  /** "" = every wall photo; else a folder or tag. */
  album: string;
  /** Only photos taken on this day (YYYY-MM-DD's month and day) in earlier years. */
  onThisDay?: string;
}

/** Up to 400 photos per day's draw; every screen gets the same draw, and its own shuffle. */
async function loadLibrary(demo: boolean, q: PhotoQuery, day: string): Promise<Photo[]> {
  const { base, body } = await feed<{ photos: Photo[] }>(demo, "photos", { album: q.album, onThisDay: q.onThisDay ?? "", limit: "400", seed: `${day}|${q.album}` });
  return (body?.photos ?? []).map((p) => resolve(base, p));
}

const today = () => new Date().toISOString().slice(0, 10);

/** Whether this is the demo household (null until known: photos wait for it). */
function useDemo(): boolean | null {
  const me = useMe();
  return me.data ? me.data.demo === true : null;
}

// ---- likes and flags ----

const MARKS_KEY = ["photos", "marks"] as const;
const NO_MARKS: PhotoMarks = { liked: [], flagged: [] };

/** Likes and flags from every screen. Flagged photos are never shown (the relay drops them too). */
export function usePhotoMarks() {
  return useQuery({
    queryKey: MARKS_KEY,
    queryFn: ({ signal }) => api<PhotoMarks>("/photos/marks", { signal }),
    staleTime: MIN,
    refetchInterval: 5 * MIN,
  });
}

/** The marks now, waiting for them the first time; a screen that can't reach Kinmantle uses what it last had. */
async function currentMarks(qc: QueryClient): Promise<PhotoMarks> {
  try {
    return await qc.ensureQueryData({ queryKey: MARKS_KEY, queryFn: () => api<PhotoMarks>("/photos/marks") });
  } catch {
    return qc.getQueryData<PhotoMarks>(MARKS_KEY) ?? NO_MARKS;
  }
}

/** Like, unlike or flag a photo. The screen changes at once; a flagged photo disappears. */
export function useMarkPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, change }: { id: string; change: { liked: boolean } | { flagged: true } }) =>
      api<PhotoMarks>(`/photos/marks/${encodeURIComponent(id)}`, { method: "PUT", body: change }),
    onMutate: async ({ id, change }) => {
      await qc.cancelQueries({ queryKey: MARKS_KEY });
      const before = qc.getQueryData<PhotoMarks>(MARKS_KEY);
      const was = before ?? NO_MARKS;
      const liked = was.liked.filter((x) => x !== id);
      qc.setQueryData<PhotoMarks>(
        MARKS_KEY,
        "flagged" in change ? { liked, flagged: [id, ...was.flagged.filter((x) => x !== id)] } : { liked: change.liked ? [id, ...liked] : liked, flagged: was.flagged },
      );
      return { before };
    },
    onError: (_e, _v, ctx) => {
      // A flag stays hidden on this screen even if it didn't save: better a missing photo than one shown.
      if (ctx?.before && !("flagged" in _v.change)) qc.setQueryData(MARKS_KEY, ctx.before);
    },
    onSuccess: (marks) => qc.setQueryData(MARKS_KEY, marks),
  });
}

export function usePhotoLibrary(album: string, onThisDay?: string) {
  const day = today();
  const demo = useDemo();
  const flagged = usePhotoMarks().data?.flagged;
  // Flagged photos drop out at once, before the next refresh.
  const select = useCallback((list: Photo[]) => (flagged?.length ? list.filter((p) => !flagged.includes(p.id)) : list), [flagged]);
  return useQuery({
    queryKey: ["photos", "library", album, onThisDay ?? "", day, demo],
    queryFn: () => loadLibrary(demo === true, { album, onThisDay }, day),
    select,
    enabled: demo !== null,
    staleTime: 5 * MIN,
    // Photos the household keeps private later drop out on the next refresh.
    refetchInterval: 10 * MIN,
  });
}

/**
 * The photo of the day, chosen by the wall computer the same way on every screen
 * (shared/photos/pick.ts). When that photo was flagged, another is picked the same way
 * from the day's draw without it, so every screen still agrees.
 */
export function usePhotoOfDay(date: string, album: string) {
  const demo = useDemo();
  const qc = useQueryClient();
  const flagged = usePhotoMarks().data?.flagged ?? [];
  return useQuery({
    queryKey: ["photos", "day", date, album, demo, flagged.join()],
    enabled: demo !== null,
    queryFn: async (): Promise<PhotoOfDay> => {
      const marks = await currentMarks(qc);
      const { base, body } = await feed<PhotoOfDay & { hidden?: boolean }>(demo === true, "day", { date, album });
      if (!body) return { date, photo: null, reason: null, yearsAgo: null };
      if (body.hidden || (body.photo && marks.flagged.includes(body.photo.id))) {
        const pool = (await loadLibrary(demo === true, { album }, date)).filter((p) => !marks.flagged.includes(p.id));
        const pick = pickPhotoOfDay(pool, date);
        return { date, photo: pick?.item ?? null, reason: pick?.reason ?? null, yearsAgo: pick?.yearsAgo ?? null };
      }
      return { ...body, photo: body.photo ? resolve(base, body.photo) : null };
    },
    placeholderData: (prev) => (prev && prev.photo && !flagged.includes(prev.photo.id) ? prev : undefined),
    staleTime: 10 * MIN,
    refetchInterval: 10 * MIN,
  });
}

export interface PhotoFeedInfo {
  count: number;
  albums: { name: string; count: number }[];
  tags: { name: string; count: number }[];
}

/** Folders and tags on the wall, for choosing which photos a widget or the screensaver shows. */
export function usePhotoFeedInfo() {
  const demo = useDemo();
  return useQuery({
    queryKey: ["photos", "info", demo],
    enabled: demo !== null,
    queryFn: async () => (await feed<PhotoFeedInfo>(demo === true, "info", {})).body ?? { count: 0, albums: [], tags: [] },
    staleTime: 30 * MIN,
  });
}
